// 📄 pages/Proyectos/EditarProyecto.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { useProject } from '../../context/ProjectContext.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { updateProject, updateErpTipo, getMemberPermisos } from '../../services/projectService.ts';
import ProyectoForm, { ProyectoFormValues } from './components/ProyectoForm.tsx';
import { modulosDe } from './modulosConfig.ts';

export default function EditarProyecto() {
  const { project, projectId, esCliente } = useProject();
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  // Mismo criterio que DetalleProyecto.tsx/ProjectSidebar.tsx: mientras
  // carga se trata como "nada oculto" — este guard es un respaldo por si
  // alguien llega DIRECTO por URL sin pasar por el botón/ítem del sidebar,
  // que ya filtran esto pero no bloquean la ruta en sí.
  const [misSeccionesOcultas, setMisSeccionesOcultas] = useState<string[]>([]);
  const [cargandoPermisos, setCargandoPermisos] = useState(true);

  useEffect(() => {
    if (!user || !projectId) { setCargandoPermisos(false); return; }
    getMemberPermisos(projectId, user.uid)
      .then((permisos) => setMisSeccionesOcultas(permisos?.seccionesOcultas || []))
      .catch((err) => console.error('Error cargando mis permisos:', err))
      .finally(() => setCargandoPermisos(false));
  }, [projectId, user]);

  if (!project || cargandoPermisos) return null;

  if (esCliente || misSeccionesOcultas.includes('editar')) {
    return <Navigate to={`/proyecto/${projectId}`} replace />;
  }

  const handleSubmit = async (values: ProyectoFormValues) => {
    await updateProject(projectId, values);
    navigate(`/proyecto/${projectId}`);
  };

  // Sub-fase K9: cambiar erpTipo es una acción "pesada" restringida al
  // creador del proyecto (o admin de plataforma) — ver firestore.rules. Se
  // oculta el botón para el resto en vez de mostrar una acción que
  // fallaría con permission-denied (mismo criterio que "Permisos" en
  // Miembros.tsx).
  const puedeCambiarErpTipo = !!user && (project.creadoPor === user.uid || profile?.role === 'admin');

  return (
    <div className="max-w-lg space-y-6">
      <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-200">
        <Pencil className="w-5 h-5 text-blue-400" /> Editar Proyecto
      </h2>

      <ProyectoForm
        orgId={project.orgId || ''}
        initialClienteId={project.clienteId}
        initialNombre={project.nombre}
        initialSolucionesActivas={project.solucionesActivas || ['commerce']}
        initialModulos={modulosDe(project)}
        initialAreas={project.areas || []}
        initialErpTipo={project.erpTipo || ''}
        initialErpVersion={project.erpVersion || ''}
        initialRecibeTickets={!!project.recibeTickets}
        submitLabel="Guardar Cambios"
        savingLabel="Guardando..."
        onSubmit={handleSubmit}
        onCancel={() => navigate(`/proyecto/${projectId}`)}
        modoEdicionProyecto
        nombreProyectoParaConfirmar={project.nombre}
        onCambiarErpTipo={
          puedeCambiarErpTipo
            ? (nuevoErpTipo) => updateErpTipo(projectId, project.erpTipo || '', nuevoErpTipo, user!.uid)
            : undefined
        }
      />
    </div>
  );
}
